import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { PublisherConfig, SelfServiceFormData } from '../types/selfService';
import { checkMinFeasibleRadius } from './minRadiusService';
import { ApiError } from '../lib/apiClient';

const DEBOUNCE_MS = 350;
const SPINNER_DELAY_MS = 200;

export interface UseRadiusRecommendationResult {
  /** Slider floor recommended by the backend. Null before the first response, or when the backend can't recommend one (no BR data / no centre / whole-station-infeasible). */
  minMiles: number | null;
  /** Backend explicitly returned null after a real compute — as opposed to "not yet computed". Used by the frontend to lock the slider at 100mi. */
  hasResponse: boolean;
  isCalculating: boolean;
  error: string | null;
  /**
   * CTV-570 v2 UX fix: names the specific input that's blocking compute.
   * When populated, the component should render a "please pick X" message
   * instead of a spinner so the buyer knows what to do next.
   */
  blockedReason: 'no-product' | 'no-budget' | 'no-dates' | 'no-center' | null;
}

/**
 * CTV-570 (radius-min v2) — hook that owns the min-feasible-radius
 * lifecycle. Watches exactly the six inputs the backend algorithm depends
 * on:
 *   1. productId (via selectedProduct)
 *   2. budget
 *   3. startDate
 *   4. endDate
 *   5. paymentPlan
 *   6. geoTargetCenter (lat, lng)
 *
 * Debounces at 350ms (matches useCampaignFeasibility), aborts in-flight
 * requests on input change, defers the spinner 200ms so a sub-200ms
 * response doesn't flicker, guards against out-of-order responses. The
 * result is lifted at the wizard root so the slider component reads a
 * stable source.
 *
 * Fires ONLY when the buyer has picked a centre (geoTargetCenter is set)
 * — otherwise there's no origin to measure from. This means the hook is
 * inert on the DMA / State tabs and while the buyer is still typing an
 * address.
 */
export function useRadiusRecommendation(
  config: PublisherConfig,
): UseRadiusRecommendationResult {
  const { watch } = useFormContext<SelfServiceFormData>();
  const budget = watch('budget');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const paymentPlan = watch('paymentPlan');
  const center = watch('geoTargetCenter');

  const productId = config.products[0]?.id;

  const inputsKey = useMemo(
    () =>
      JSON.stringify({
        productId,
        budget,
        startDate,
        endDate,
        paymentPlan,
        centerLat: center?.lat ?? null,
        centerLng: center?.lng ?? null,
      }),
    [productId, budget, startDate, endDate, paymentPlan, center?.lat, center?.lng],
  );

  const [minMiles, setMinMiles] = useState<number | null>(null);
  const [hasResponse, setHasResponse] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockedReason, setBlockedReason] = useState<
    'no-product' | 'no-budget' | 'no-dates' | 'no-center' | null
  >(null);

  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spinnerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Gate: all required inputs must be valid before firing. Populate
    // `blockedReason` so the component can render a specific "please pick
    // X" message instead of a generic spinner.
    const gate = (
      reason: 'no-product' | 'no-budget' | 'no-dates' | 'no-center',
    ) => {
      setMinMiles(null);
      setHasResponse(false);
      setIsCalculating(false);
      setError(null);
      setBlockedReason(reason);
    };
    if (!productId) return gate('no-product');
    if (!budget || budget <= 0) return gate('no-budget');
    if (!startDate || !endDate) return gate('no-dates');
    if (endDate <= startDate) return gate('no-dates');
    if (!center) return gate('no-center');

    setBlockedReason(null);
    // Clear the previous response as inputs change so the slider shows a
    // spinner instead of stale state until the new answer lands.
    setHasResponse(false);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const reqId = ++requestIdRef.current;

      if (spinnerTimerRef.current) clearTimeout(spinnerTimerRef.current);
      spinnerTimerRef.current = setTimeout(() => {
        if (reqId === requestIdRef.current) setIsCalculating(true);
      }, SPINNER_DELAY_MS);

      checkMinFeasibleRadius(
        {
          productId: productId!,
          budget: budget!,
          startDate,
          endDate,
          paymentPlan: paymentPlan ?? 'upfront',
          centerLat: center.lat,
          centerLng: center.lng,
          includeDiagnostic: import.meta.env.DEV,
        },
        ctrl.signal,
      )
        .then((data) => {
          if (reqId !== requestIdRef.current) return;
          setMinMiles(data.minFeasibleMiles);
          setHasResponse(true);
          setError(null);
        })
        .catch((e) => {
          if (reqId !== requestIdRef.current) return;
          const name = (e as { name?: string })?.name;
          if (name === 'AbortError' || name === 'CanceledError') return;
          // Rate-limited (429) is "slow down", not "broken"; say so.
          setError(
            e instanceof ApiError && e.status === 429
              ? 'You are making changes quickly. Give it a few seconds and adjust again.'
              : 'Could not compute minimum radius. Try again in a moment.',
          );
          setMinMiles(null);
          setHasResponse(false);
        })
        .finally(() => {
          if (spinnerTimerRef.current) {
            clearTimeout(spinnerTimerRef.current);
            spinnerTimerRef.current = null;
          }
          if (reqId === requestIdRef.current) setIsCalculating(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsKey]);

  return { minMiles, hasResponse, isCalculating, error, blockedReason };
}
